// components/MaterialSearchModal.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ─── Helpers ───

function tokenise(text) {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).map(w => w.trim()).filter(w => w.length >= 2);
}

function levenshtein(a, b) {
  const la = a.length, lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;
  let prev = Array.from({ length: lb + 1 }, (_, i) => i);
  let curr = new Array(lb + 1);
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    for (let j = 1; j <= lb; j++) {
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

function findClosestWords(token, vocabulary, maxDistance = 2, maxResults = 3) {
  const t = token.toLowerCase();
  const eff = t.length <= 3 ? Math.min(maxDistance, 1) : maxDistance;
  const cands = [];
  for (const word of vocabulary) {
    if (Math.abs(word.length - t.length) > eff) continue;
    const d = levenshtein(t, word);
    if (d > 0 && d <= eff) cands.push({ word, distance: d });
  }
  cands.sort((a, b) => a.distance - b.distance);
  return cands.slice(0, maxResults);
}

const getName = (c) =>
  c?.MeasurementUnitName || c?.CostMeasurementName || c?.Name || c?.Description || '';

function matchesFuzzy(query, candidate) {
  const tokens = tokenise(query);
  if (tokens.length === 0) return true;
  const hay = candidate.toLowerCase();
  const words = tokenise(hay);
  return tokens.every(tok => {
    if (hay.includes(tok)) return true;
    const max = tok.length <= 4 ? 1 : 2;
    return words.some(w => Math.abs(w.length - tok.length) <= max && levenshtein(tok, w) <= max);
  });
}

const STOP_WORDS = new Set(['the', 'and', 'for', 'with', 'from', 'this', 'that', 'per', 'mm', 'mpa', 'kg']);

function buildVocab(materials) {
  const v = new Set();
  materials.forEach(m => {
    tokenise(getName(m)).forEach(t => v.add(t));
    tokenise(m.Category || '').forEach(t => v.add(t));
  });
  return [...v];
}

function buildSuggestionIndex(materials) {
  const related = {};
  const allTerms = new Set();
  materials.forEach(m => {
    const name = getName(m);
    const cat = m.Category || '';
    const nToks = tokenise(name);
    const cToks = tokenise(cat);
    allTerms.add(cat);
    nToks.filter(t => t.length > 3 && !STOP_WORDS.has(t)).forEach(t => allTerms.add(t));
    const display = [cat, ...nToks.filter(t => t.length > 3 && !STOP_WORDS.has(t))];
    [...new Set([...nToks, ...cToks])].forEach(tok => {
      display.forEach(dn => {
        if (tok === dn.toLowerCase() || STOP_WORDS.has(tok)) return;
        if (!related[tok]) related[tok] = new Set();
        related[tok].add(dn);
      });
    });
  });
  return { related, allTerms: [...allTerms] };
}

function getSuggestions(query, index, max = 10) {
  if (!query.trim()) return [];
  const toks = tokenise(query);
  if (!toks.length) return [];
  const scored = {};
  toks.forEach(qt => {
    Object.keys(index.related).forEach(kw => {
      if (kw.includes(qt) || qt.includes(kw)) {
        index.related[kw].forEach(t => { scored[t] = (scored[t] || 0) + 1; });
      }
    });
    index.allTerms.forEach(t => {
      if (t.toLowerCase().includes(qt)) scored[t] = (scored[t] || 0) + 0.5;
    });
  });
  delete scored[query.toLowerCase().trim()];
  return Object.entries(scored).sort((a, b) => b[1] - a[1]).slice(0, max).map(([t]) => t);
}

// ─── Component ───

const MaterialSearchModal = ({
  isOpen, onClose, onSelect,
  materials = [],
  carbonCategories = [],
  biogenicCategories = [],
  weightCategories = [],
  title = '',
  // false for lists without carbon/weight values (e.g. template materials) — hides those columns
  showMetrics = true,
  // optional tabs over the list, e.g. [{ key: 'all', label: 'All materials' },
  // { key: 'mine', label: 'My approved materials', filter: m => …, empty: 'text when none' }]
  tabs = null,
}) => {
  const [searchText, setSearchText] = useState('');
  const [describeText, setDescribeText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeSuggestions, setActiveSuggestions] = useState([]);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeTab, setActiveTab] = useState(tabs?.[0]?.key || null);
  const tab = (tabs || []).find(t => t.key === activeTab) || null;
  // the list the rest of the modal works on: the active tab's slice of `materials`
  const tabMaterials = useMemo(
    () => (tab?.filter ? materials.filter(tab.filter) : materials),
    [materials, tab]
  );
  const tabCounts = useMemo(
    () => Object.fromEntries((tabs || []).map(t => [t.key, t.filter ? materials.filter(t.filter).length : materials.length])),
    [materials, tabs]
  );
  const searchRef = useRef(null);

  const vocabulary = useMemo(() => buildVocab(materials), [materials]);
  const suggestionIndex = useMemo(() => buildSuggestionIndex(materials), [materials]);

  // Build value lookups by material name
  const valueLookup = useMemo(() => {
    const co2Map = {};
    const bioMap = {};
    const weightMap = {};

    // If separate arrays are provided, use them
    if (carbonCategories.length > 0) {
      carbonCategories.forEach(m => { const n = getName(m); if (n && m.Value != null) co2Map[n] = Number(m.Value); });
    }
    if (biogenicCategories.length > 0) {
      biogenicCategories.forEach(m => { const n = getName(m); if (n && m.Value != null) bioMap[n] = Number(m.Value); });
    }
    if (weightCategories.length > 0) {
      weightCategories.forEach(m => { const n = getName(m); if (n && m.Value != null) weightMap[n] = Number(m.Value); });
    }

    // Fallback: if no separate arrays, try to extract from the main materials array
    // (materials = ENV_ALL which contains all three types mixed together)
    if (carbonCategories.length === 0 && biogenicCategories.length === 0 && weightCategories.length === 0) {
      materials.forEach(m => {
        const n = getName(m);
        if (!n || m.Value == null) return;
        const mid = (m.MeasurementId || '').toLowerCase();
        if (mid.includes('biogenic')) { bioMap[n] = Number(m.Value); }
        else if (mid.includes('weight')) { weightMap[n] = Number(m.Value); }
        else if (mid.includes('co2')) { co2Map[n] = Number(m.Value); }
        else if (mid.includes('rate')) { /* skip cost */ }
        else { co2Map[n] = Number(m.Value); } // default to co2
      });
    }

    return { co2: co2Map, bio: bioMap, weight: weightMap };
  }, [materials, carbonCategories, biogenicCategories, weightCategories]);

  const allCategories = useMemo(() => {
    const cats = new Set();
    materials.forEach(m => { if (m.Category) cats.add(m.Category); });
    return [...cats].sort();
  }, [materials]);

  const categoryOptions = useMemo(() =>
    [{ value: '', label: 'All categories' }, ...allCategories.map(c => ({ value: c, label: c }))],
    [allCategories]
  );

  const suggestions = useMemo(
    () => getSuggestions(describeText, suggestionIndex),
    [describeText, suggestionIndex]
  );

  const spellingCorrections = useMemo(() => {
    if (!searchText.trim()) return [];
    const toks = tokenise(searchText);
    const corrs = [];
    toks.forEach(tok => {
      const hasMatch = vocabulary.some(v => v.includes(tok) || tok.includes(v));
      if (!hasMatch) {
        const closest = findClosestWords(tok, vocabulary);
        if (closest.length > 0) corrs.push({ original: tok, suggestions: closest.map(c => c.word) });
      }
    });
    return corrs;
  }, [searchText, vocabulary]);

  const filteredByCategory = useMemo(() => {
    const combinedSearch = [searchText, ...activeSuggestions].join(' ').trim();
    let filtered = tabMaterials;

    if (selectedCategory) {
      filtered = filtered.filter(m => m.Category === selectedCategory);
    }

    if (combinedSearch) {
      filtered = filtered.filter(m => {
        // SearchKeys: extra text a caller wants matched (e.g. a template material's id)
        const fullText = `${getName(m)} ${m.Category || ''} ${m.SearchKeys || ''}`;
        return matchesFuzzy(combinedSearch, fullText);
      });
    }

    const grouped = {};
    filtered.forEach(m => {
      const cat = m.Category || 'Other';
      const name = getName(m);
      if (!name) return;
      if (!grouped[cat]) grouped[cat] = [];
      // DedupeKey: callers whose items can share a name (two templates called the same) keep both
      const key = m.DedupeKey || name;
      if (!grouped[cat].some(x => x.key === key)) {
        grouped[cat].push({ name, category: cat, raw: m, key });
      }
    });

    return grouped;
  }, [tabMaterials, searchText, selectedCategory, activeSuggestions]);

  const totalResults = useMemo(
    () => Object.values(filteredByCategory).reduce((sum, arr) => sum + arr.length, 0),
    [filteredByCategory]
  );

  // Auto-expand when searching
  useEffect(() => {
    const isSearching = searchText.trim() || activeSuggestions.length > 0;
    if (isSearching) {
      const expanded = {};
      Object.keys(filteredByCategory).forEach(cat => { expanded[cat] = true; });
      setExpandedCategories(expanded);
    }
  }, [searchText, activeSuggestions, filteredByCategory]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 150);
      setSearchText('');
      setDescribeText('');
      setSelectedCategory('');
      setActiveSuggestions([]);
      setExpandedCategories({});
      setActiveTab(tabs?.[0]?.key || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // a short tab (e.g. "My approved materials") opens with every group expanded
  useEffect(() => {
    if (!tab || !tab.filter) return;
    const expanded = {};
    tabMaterials.forEach(m => { expanded[m.Category || 'Other'] = true; });
    setExpandedCategories(expanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleSelect = (item) => {
    onSelect(item.name, item.category, item.raw);
  };

  const toggleSuggestion = (term) => {
    setActiveSuggestions(prev =>
      prev.includes(term) ? prev.filter(t => t !== term) : [...prev, term]
    );
  };

  const applyCorrection = (original, corrected) => {
    const re = new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    setSearchText(prev => prev.replace(re, corrected));
  };

  if (!isOpen) return null;

  // Render via portal to avoid parent DOM interference
  const modalContent = (
    <div
      style={S.overlay}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={S.modal}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerTitle}>{title || 'Search Materials'}</div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {tabs && tabs.length > 1 && (
          <div style={S.tabBar} role="tablist">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={t.key === activeTab}
                style={{ ...S.tab, ...(t.key === activeTab ? S.tabActive : {}) }}
                onClick={() => { setActiveTab(t.key); setSelectedCategory(''); }}
              >
                {t.label} <span style={S.tabCount}>({tabCounts[t.key] ?? 0})</span>
              </button>
            ))}
          </div>
        )}

        {/* Search bar */}
        <div style={S.searchBar}>
          <div style={S.searchInputWrapper}>
            <span style={S.searchIcon}>🔍</span>
            <input
              ref={searchRef}
              style={S.searchInput}
              type="text"
              placeholder="Name, category, or description..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && <button style={S.clearBtn} onClick={() => setSearchText('')}>✕</button>}
          </div>

          <div style={S.advancedSection}>
            <div style={S.advancedLabel}>
              <span style={{ fontSize: 12, color: '#999' }}>⚙</span>
              <span>ADVANCED SEARCH</span>
            </div>
            <div style={S.advancedRow}>
              <div style={S.advancedField}>
                <label style={S.fieldLabel}>Category</label>
                <select style={S.select} value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
                  {categoryOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div style={S.advancedField}>
                <label style={S.fieldLabel}>Describe what you're looking for</label>
                <div style={S.describeWrapper}>
                  <span style={{ fontSize: 13, color: '#999' }}>💡</span>
                  <input style={S.describeInput} type="text" placeholder="e.g. Concrete, Timber..." value={describeText}
                    onChange={e => setDescribeText(e.target.value)} />
                  {describeText && <button style={S.clearBtn} onClick={() => { setDescribeText(''); setActiveSuggestions([]); }}>✕</button>}
                </div>
              </div>
            </div>
          </div>

          {spellingCorrections.length > 0 && (
            <div style={S.spellingBar}>
              Did you mean:{' '}
              {spellingCorrections.map(c => (
                <span key={c.original}>
                  {c.suggestions.map((s, i) => (
                    <span key={s}>
                      <span style={S.spellingLink} onClick={() => applyCorrection(c.original, s)}>{s}</span>
                      {i < c.suggestions.length - 1 && ' / '}
                    </span>
                  ))}
                  <span style={{ color: '#999', marginLeft: 4, fontSize: 11 }}>(for "{c.original}")</span>
                </span>
              ))}
            </div>
          )}

          {suggestions.length > 0 && (
            <div style={S.chipsBar}>
              <span style={{ fontSize: 11, color: '#999', marginRight: 6 }}>Related:</span>
              {suggestions.map(term => {
                const isActive = activeSuggestions.includes(term);
                return (
                  <span key={term} style={{ ...S.chip, ...(isActive ? S.chipActive : {}) }}
                    onClick={() => toggleSuggestion(term)}>
                    {term} {isActive && '✕'}
                  </span>
                );
              })}
            </div>
          )}

          {activeSuggestions.length > 0 && (
            <div style={{ ...S.chipsBar, background: '#fff', borderBottom: '1px solid #E0E0E0' }}>
              <span style={{ fontSize: 11, color: '#666', marginRight: 6 }}>Filters:</span>
              {activeSuggestions.map(term => (
                <span key={term} style={{ ...S.chip, ...S.chipActive }} onClick={() => toggleSuggestion(term)}>{term} ✕</span>
              ))}
              <span style={{ ...S.chip, color: '#999' }} onClick={() => setActiveSuggestions([])}>Clear all</span>
            </div>
          )}
        </div>

        {/* Table header */}
        <div style={S.tableHeader}>
          <span style={{ flex: 2 }}>Name</span>
          <span style={{ width: 100, textAlign: 'center' }}>Unit</span>
          {showMetrics && (
            <>
              <span style={{ width: 110, textAlign: 'right' }}>CO₂ (kgCO₂)</span>
              <span style={{ width: 110, textAlign: 'right' }}>CO₂-Biogenic</span>
              <span style={{ width: 100, textAlign: 'right' }}>Weight (kg)</span>
            </>
          )}
        </div>

        {/* Category list */}
        <div style={S.listContainer}>
          {Object.entries(filteredByCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cat, items]) => (
              <div key={cat}>
                <div
                  style={{ ...S.categoryRow, ...(expandedCategories[cat] ? S.categoryRowExpanded : {}) }}
                  onClick={() => toggleCategory(cat)}
                >
                  <span style={S.categoryArrow}>{expandedCategories[cat] ? '▾' : '▸'}</span>
                  <span style={S.categoryName}>{cat}</span>
                  <span style={S.categoryCount}>({items.length})</span>
                </div>

                {expandedCategories[cat] && items.map((item, idx) => {
                  const co2Val = valueLookup.co2[item.name];
                  const bioVal = valueLookup.bio[item.name];
                  const weightVal = valueLookup.weight[item.name];

                  return (
                    <div
                      key={`${item.key}-${idx}`}
                      style={{ ...S.materialRow, ...(hoveredItem === `${cat}-${idx}` ? S.materialRowHover : {}) }}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHoveredItem(`${cat}-${idx}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                        {item.name}
                      </span>
                      <span style={{ width: 100, textAlign: 'center', color: '#666', fontSize: 12 }}>
                        {item.raw?.QuantityTypeName || ''}
                      </span>
                      {showMetrics && (
                        <>
                          <span style={{ width: 110, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#2E7D32' }}>
                            {co2Val != null ? co2Val.toFixed(4) : '—'}
                          </span>
                          <span style={{ width: 110, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#00695C' }}>
                            {bioVal != null ? bioVal.toFixed(4) : '—'}
                          </span>
                          <span style={{ width: 100, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#546E7A' }}>
                            {weightVal != null ? weightVal.toFixed(4) : '—'}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

          {totalResults === 0 && (searchText || activeSuggestions.length > 0) && (
            <div style={S.noResults}>No materials found. Try different search terms.</div>
          )}
          {tab?.filter && tabMaterials.length === 0 && (
            <div style={S.noResults}>{tab.empty || 'Nothing here yet.'}</div>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <span style={{ fontSize: 12, color: '#999' }}>
            Click a material row to select · {totalResults} material{totalResults !== 1 ? 's' : ''} shown
          </span>
          <button style={S.cancelBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // Render via portal to document.body
  return ReactDOM.createPortal(modalContent, document.body);
};

// ─── Styles ───

const S = {
  tabBar: { display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid #E5E7EB' },
  tab: {
    border: 'none', background: 'none', padding: '8px 14px', fontSize: 13, fontWeight: 600,
    color: '#6B7280', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1,
  },
  tabActive: { color: '#2A7F73', borderBottomColor: '#2A7F73' },
  tabCount: { fontWeight: 500, color: '#9CA3AF', fontSize: 12 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  },
  modal: {
    background: '#fff', borderRadius: 12, width: 'min(960px, 95vw)', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #E0E0E0', flexShrink: 0,
  },
  headerTitle: {
    fontSize: 15, fontWeight: 600, color: '#243c4c',
    fontFamily: "'Gotham Rounded', sans-serif",
  },
  closeBtn: {
    width: 32, height: 32, border: '1px solid #E0E0E0', borderRadius: 6,
    background: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#666',
  },
  searchBar: { flexShrink: 0, borderBottom: '1px solid #E0E0E0' },
  searchInputWrapper: {
    display: 'flex', alignItems: 'center', margin: '12px 20px 8px',
    border: '1.5px solid #C8CED8', borderRadius: 8, height: 44, padding: '0 12px',
    background: '#F5F7FA',
  },
  searchIcon: { marginRight: 8, fontSize: 16, color: '#4A9B8F' },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent',
    fontFamily: "'Gotham Rounded', sans-serif", color: '#243c4c',
  },
  clearBtn: {
    border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF',
    fontSize: 14, padding: '0 4px', lineHeight: 1,
  },
  advancedSection: { padding: '0 20px 12px' },
  advancedLabel: {
    display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600,
    color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
  },
  advancedRow: { display: 'flex', gap: 12 },
  advancedField: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  fieldLabel: { fontSize: 11, fontWeight: 500, color: '#666' },
  select: {
    height: 36, borderRadius: 6, border: '1px dashed #ddd', padding: '0 8px',
    fontSize: 13, fontFamily: "'Gotham Rounded', sans-serif", color: '#243c4c',
    background: '#fff', outline: 'none', cursor: 'pointer',
  },
  describeWrapper: {
    display: 'flex', alignItems: 'center', height: 36, borderRadius: 6,
    border: '1px dashed #ddd', padding: '0 8px', background: '#fff', gap: 6,
  },
  describeInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent',
    fontFamily: "'Gotham Rounded', sans-serif", color: '#243c4c',
  },
  spellingBar: {
    padding: '6px 20px', borderTop: '1px solid #f0f0f0', background: '#FFF8E1',
    fontSize: 12, color: '#666',
  },
  spellingLink: {
    color: '#4A9B8F', fontWeight: 600, cursor: 'pointer',
    textDecoration: 'underline', textDecorationStyle: 'dotted',
  },
  chipsBar: {
    display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 20px',
    borderTop: '1px solid #f0f0f0', background: '#FAFBFC',
  },
  chip: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11,
    fontWeight: 500, cursor: 'pointer', border: '1px solid #E0E0E0',
    background: '#fff', color: '#555', userSelect: 'none',
  },
  chipActive: { background: '#4A9B8F', color: '#fff', borderColor: '#4A9B8F' },
  tableHeader: {
    display: 'flex', padding: '10px 20px', fontSize: 12, fontWeight: 600,
    color: '#666', background: '#F5F7FA', borderBottom: '1px solid #E0E0E0',
    flexShrink: 0, gap: 8,
  },
  listContainer: { flex: 1, overflowY: 'auto' },
  categoryRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
    cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
    fontSize: 13, fontWeight: 600, color: '#243c4c', userSelect: 'none',
  },
  categoryRowExpanded: { background: '#F0FAF8' },
  categoryArrow: { fontSize: 12, color: '#4A9B8F', width: 16, flexShrink: 0 },
  categoryName: { flex: 1 },
  categoryCount: { fontSize: 11, color: '#999', fontWeight: 400 },
  materialRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px 8px 36px',
    cursor: 'pointer', borderBottom: '1px solid #f8f8f8', fontSize: 13, color: '#243c4c',
  },
  materialRowHover: { background: '#E6F3F7' },
  noResults: { padding: '32px 20px', fontSize: 13, color: '#999', textAlign: 'center' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', borderTop: '1px solid #E0E0E0', flexShrink: 0, background: '#FAFBFC',
  },
  cancelBtn: {
    padding: '8px 20px', borderRadius: 8, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
    color: '#666', fontFamily: "'Gotham Rounded', sans-serif",
  },
};

export default MaterialSearchModal;